import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, X } from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { apiGet, apiPatch } from '../lib/api';
import { getEntityUrl, type EntitySearchResult } from '../lib/entityUrls';
import { getEntityIcon, getEntityColor, type EntityType } from './EntityChip';
import { DossierEditor } from './editor/DossierEditor';

const ENDPOINTS: Record<EntityType, (id: string) => string> = {
  task: id => `/tasks/${id}`,
  note: id => `/notes/${id}`,
  meeting_instance: id => `/meeting-instances/${id}`,
  meeting_series: id => `/meeting-series/${id}`,
  person: id => `/people/${id}`,
  tag: id => `/tags/${id}`,
  scope: id => `/scopes/${id}`,
  reference_doc: id => `/views/${id}`,
  quick_capture: id => `/quick-captures/${id}`,
};

interface EntityData {
  title?: string; name?: string; content?: unknown; series_id?: string;
  status?: string; due_date?: string; meeting_date?: string;
  // quick_capture rows (GET /api/quick-captures/:id) expose their body as
  // `text` — a plain string, not TipTap JSON — with no `title`/`content`.
  text?: string;
  [k: string]: unknown;
}

function truncate(s: string, max = 80): string {
  const trimmed = s.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max).trimEnd()}…` : trimmed;
}

export function EntityPreviewModal({ type, id, onClose }: {
  type: EntityType; id: string; onClose: () => void;
}) {
  const navigate = useNavigate();
  const [data, setData] = useState<EntityData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');
  const Icon = getEntityIcon(type);
  const color = getEntityColor(type);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    apiGet<EntityData>(ENDPOINTS[type](id))
      .then(d => { if (!cancelled) { setData(d); setState('ready'); } })
      .catch(() => { if (!cancelled) setState('missing'); });
    return () => { cancelled = true; };
  }, [type, id]);

  const quickCaptureText = type === 'quick_capture' && typeof data?.text === 'string' ? data.text : undefined;
  const title = data?.title || data?.name
    || (quickCaptureText ? truncate(quickCaptureText) : undefined)
    || (state === 'missing' ? 'No longer exists' : 'Untitled');

  function openFull() {
    const url = getEntityUrl({
      entity_type: type, entity_id: id,
      metadata: data?.series_id ? { series_id: data.series_id } : undefined,
    } as EntitySearchResult);
    onClose();
    navigate(url);
  }

  return (
    <div
      className="entity-preview-overlay"
      role="dialog"
      aria-label={`Preview: ${title}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="entity-preview-box">
        <header className="entity-preview-header">
          <Icon size={16} style={{ color }} />
          <span className="entity-preview-title">{title}</span>
          <button type="button" onClick={openFull} className="entity-preview-open" title="Open full page">
            <ExternalLink size={14} /> Open
          </button>
          <button type="button" onClick={onClose} aria-label="Close" className="entity-preview-close">
            <X size={16} />
          </button>
        </header>
        <div className="entity-preview-body">
          {state === 'loading' && <p className="entity-preview-muted">Loading…</p>}
          {state === 'missing' && <p className="entity-preview-muted">This item no longer exists.</p>}
          {state === 'ready' && data && <EntityPreviewContent type={type} id={id} data={data} onChange={setData} />}
        </div>
      </div>
    </div>
  );
}

function fmtDate(v?: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString();
}

// TipTap doc bodies are stored as a JSON *string* in the DB and returned that
// way by the entity GET endpoints (same as NoteDetailPage, which JSON.parses
// before rendering). Passing the raw string to DossierEditor makes TipTap treat
// it as an HTML string and render the literal JSON — so parse it here. Accepts
// an already-parsed object too, for endpoints that return content pre-parsed.
function parseDoc(content: unknown): JSONContent | null {
  if (!content) return null;
  if (typeof content === 'string') {
    try { return JSON.parse(content) as JSONContent; } catch { return null; }
  }
  if (typeof content === 'object') return content as JSONContent;
  return null;
}

function EntityPreviewContent({ type, id, data, onChange }: {
  type: EntityType; id: string; data: EntityData; onChange: (d: EntityData) => void;
}) {
  const fields: { label: string; value: string }[] = [];
  if (typeof data.status === 'string') fields.push({ label: 'Status', value: data.status });
  const due = fmtDate(data.due_date); if (due) fields.push({ label: 'Due', value: due });
  const met = fmtDate(data.meeting_date); if (met) fields.push({ label: 'Date', value: met });

  const isTask = type === 'task';
  const done = data.status === 'done';

  async function toggleDone() {
    const prevStatus = data.status;
    const next = done ? 'todo' : 'done';
    onChange({ ...data, status: next });
    try {
      await apiPatch(`/tasks/${id}`, { status: next });
    } catch {
      onChange({ ...data, status: prevStatus });
    }
  }

  // `data.content` (TipTap doc JSON) and quick-capture `data.text` (plain
  // string) are mutually exclusive fields depending on entity type — render
  // whichever is present, never pass plain text through DossierEditor.
  const bodyDoc = parseDoc(data.content);
  const bodyText = type === 'quick_capture' && typeof data.text === 'string' ? data.text : undefined;

  return (
    <div className="entity-preview-content">
      {fields.length > 0 && (
        <dl className="entity-preview-fields">
          {fields.map(f => (
            <div key={f.label}><dt>{f.label}</dt><dd>{f.value}</dd></div>
          ))}
        </dl>
      )}
      {isTask && (
        <button type="button" className="entity-preview-action" onClick={toggleDone}>
          {done ? 'Mark not done' : 'Mark done'}
        </button>
      )}
      {bodyDoc && (
        <div className="entity-preview-doc">
          <DossierEditor content={bodyDoc} editable={false} onUpdate={() => {}} />
        </div>
      )}
      {!bodyDoc && bodyText && (
        <div className="entity-preview-doc entity-preview-text" style={{ whiteSpace: 'pre-wrap' }}>
          {bodyText}
        </div>
      )}
    </div>
  );
}
